import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  HStack,
  VStack,
  Box,
  Text,
  Badge,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { capitalize } from "lodash";
import GarrazCopyText from "./GaraazCopyText";

export interface VoidedInvoice {
  invoiceType: "ORDER" | "DELIVERY";
  invoiceNo?: string;
  invoiceMediaId?: string;
  voidedByName?: string;
  voidedBy?: string;
  voidReason?: string;
}

interface VoidedInvoicesNestedPopoverProps {
  voidedInvoices: VoidedInvoice[];
}

const VoidedInvoicesNestedPopover: React.FC<
  VoidedInvoicesNestedPopoverProps
> = ({ voidedInvoices }) => {
  return (
    <Popover
      trigger="hover"
      placement="bottom-end"
      gutter={8}
      offset={[0, 4]}
      isLazy
      strategy="fixed"
    >
      <PopoverTrigger>
        <Badge
          borderRadius="md"
          px={2}
          py={1}
          fontSize="xs"
          fontWeight="medium"
          textTransform="none"
          cursor="pointer"
          display="inline-flex"
          alignItems="center"
          borderWidth="1px"
          borderColor="green.200"
          bg="green.100"
          color="green.700"
          transition="background 0.15s ease, border-color 0.15s ease"
          _hover={{
            bg: "green.200",
            borderColor: "green.300",
          }}
          _active={{ bg: "green.300" }}
        >
          Voids ({voidedInvoices.length})
        </Badge>
      </PopoverTrigger>

      {/* Keep in-tree (no nested Portal) so parent InvoicePopover hover does not auto-close. */}
      <PopoverContent
        width="260px"
        boxShadow="lg"
        borderColor="gray.200"
        zIndex={2000}
        _focus={{ outline: "none" }}
      >
        <PopoverHeader
          fontWeight="semibold"
          fontSize="sm"
          borderBottomWidth="1px"
          bg="gray.50"
        >
          Voided Invoices ({voidedInvoices.length})
        </PopoverHeader>

        <PopoverBody maxH="350px" overflowY="auto" p={2}>
          <VStack align="stretch" spacing={1.5}>
            {voidedInvoices.map((item, index) => (
              <Box
                key={index}
                p={2}
                borderWidth="1px"
                borderRadius="md"
                borderColor="gray.200"
                bg="white"
                _hover={{
                  borderColor: "green.300",
                  boxShadow: "sm",
                }}
                transition="all 0.2s"
              >
                <HStack spacing={1} align="center" mb={0.5}>
                  <Text fontSize="xs" color="gray.500" flexShrink={0}>
                    Type
                  </Text>
                  <Text fontSize="xs" color="gray.500" flexShrink={0}>
                    :
                  </Text>
                  <Text fontSize="xs" color="gray.700" noOfLines={1} flex={1}>
                    {capitalize(item.invoiceType.toLowerCase())}
                  </Text>
                </HStack>

                {item.invoiceNo && (
                  <HStack spacing={1} align="center" flexWrap="wrap">
                    <Text fontSize="xs" color="gray.500" flexShrink={0}>
                      Invoice No
                    </Text>
                    <Text fontSize="xs" color="gray.500" flexShrink={0}>
                      :
                    </Text>
                    <HStack spacing={1} align="center" minW={0} maxW="100%">
                      <Tooltip label={item.invoiceNo}>
                        <Text
                          fontSize="xs"
                          color="gray.700"
                          noOfLines={1}
                          maxW={{ base: "140px", sm: "170px" }}
                        >
                          {item.invoiceNo}
                        </Text>
                      </Tooltip>
                      <Box flexShrink={0} display="inline-flex">
                        <GarrazCopyText
                          text={item.invoiceNo}
                          popOverMessage="Invoice No Copied"
                        />
                      </Box>
                    </HStack>
                  </HStack>
                )}

                <HStack spacing={1} align="center" mt={0.5}>
                  <Text fontSize="xs" color="gray.500" flexShrink={0}>
                    Voided By
                  </Text>
                  <Text fontSize="xs" color="gray.500" flexShrink={0}>
                    :
                  </Text>
                  <Text fontSize="xs" color="gray.700" noOfLines={1} flex={1}>
                    {item.voidedByName ?? "—"}
                  </Text>
                </HStack>

                {item.voidReason && (
                  <Tooltip
                    label={item.voidReason}
                    placement="bottom"
                    hasArrow
                    isDisabled={item.voidReason.length <= 25}
                  >
                    <HStack spacing={1} align="center" mt={0.5}>
                      <Text fontSize="xs" color="gray.500" flexShrink={0}>
                        Reason
                      </Text>
                      <Text fontSize="xs" color="gray.500" flexShrink={0}>
                        :
                      </Text>
                      <Box flex={1}>
                        <Text fontSize="xs" color="gray.700">
                          {item.voidReason.length > 25
                            ? `${item.voidReason.substring(0, 25)}...`
                            : item.voidReason}
                        </Text>
                      </Box>
                    </HStack>
                  </Tooltip>
                )}
              </Box>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default VoidedInvoicesNestedPopover;
